import { memo } from "react";
import { formatDistanceToNow } from "date-fns";
import { Ticket } from "lucide-react";
import { notificationTypeIcons } from "@/components/notifications/notification-type-icons";
import { NotificationMarkdown } from "@/components/notifications/notification-markdown";
import { IconContainer } from "@/components/ui/icon-container";
import type { UnifiedNotification } from "@/lib/api/unified-notifications";

const notificationTypeCategory: Record<string, string> = {
  ticket_created: "TICKET",
  ticket_closed: "TICKET",
  ticket_status_changed: "TICKET",
  document_created: "KB",
  scrape_completed: "SCRAPE",
  scrape_failed: "SCRAPE",
  modmail_received: "MODMAIL",
  admin_announcement: "ADMIN",
  admin_maintenance: "SYSTEM",
  admin_feature_update: "UPDATE",
  admin_custom: "ADMIN",
  subscription_changed: "BILLING",
  team_invite: "TEAM",
};

interface NotificationItemProps {
  notification: UnifiedNotification;
  onMarkRead: (id: number) => void;
}

export const NotificationItem = memo(function NotificationItem({
  notification,
  onMarkRead,
}: NotificationItemProps) {
  const Icon = notificationTypeIcons[notification.type] ?? Ticket;
  const isUnread = !notification.isRead;
  const category = notificationTypeCategory[notification.type] ?? "OTHER";

  return (
    <button
      type="button"
      className={`group relative flex w-full gap-3 border-b px-5 py-3.5 text-left transition-colors duration-200 ${
        isUnread
          ? "bg-rose-500/[0.03] hover:bg-rose-500/[0.07]"
          : "hover:bg-muted/50"
      }`}
      onClickCapture={() => {
        if (isUnread) onMarkRead(notification.id);
      }}
    >
      {/* Unread accent */}
      {isUnread && (
        <div className="absolute inset-y-0 left-0 w-[3px] bg-rose-500" />
      )}

      {/* Icon */}
      <IconContainer
        icon={Icon}
        size="sm"
        variant={isUnread ? "accent" : "muted"}
      />

      {/* Content */}
      <div className="min-w-0 flex-1">
        {/* Title row */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <span
              className={`truncate text-sm leading-tight ${isUnread ? "font-semibold text-rose-500" : "text-muted-foreground"}`}
            >
              {notification.title}
            </span>
            {isUnread && (
              <span className="size-1.5 shrink-0 rounded-full bg-rose-500" />
            )}
          </div>
          <span className="text-muted-foreground/50 shrink-0 text-[11px]">
            {formatDistanceToNow(new Date(notification.createdAt), {
              addSuffix: true,
            })}
          </span>
        </div>

        {/* Message */}
        {notification.message && (
          <div className="mt-1 break-words">
            <NotificationMarkdown content={notification.message} />
          </div>
        )}

        {/* Category tag */}
        <div className="mt-1.5">
          <span className="text-muted-foreground/40 font-mono text-[10px]">
            {category}
          </span>
        </div>
      </div>
    </button>
  );
});
